import React, { useEffect, useState, useRef } from 'react';
import './Header.scss';
import { useAppSelector, useAppDispatch } from 'modules';
import { useLocation, useHistory } from 'react-router';
import { ChevronLeft } from 'react-feather';
import { useTranslation } from 'react-i18next';
import store from 'store';
import { KEY } from 'utils/constants/storageKey';
import Greeting from '../Greeting';
import { ReactComponent as Logo } from '../../../assets/logoBeconTxt.svg';
import { ReactComponent as Home } from '../../../assets/home_header.svg';
import { ReactComponent as Cart } from 'assets/cart.svg';
import { exitKbWebViewForKb } from 'utils/kb';
import { goToMallMypage, goToMallCartV2Page } from 'utils/getMallUrl';
import {
  checkFirstOpen,
  setNavTab,
  setPageType,
  setPageTitle,
  getMainCurrentTab,
} from '../../../modules/status';
import { FROM } from 'utils/constants/storageKey';
import { getMedicalReceptionStep, setStep } from 'modules/telemedicine';

const Header = ({ isTeleMedicineService }) => {
  const [headerVisible, setHeaderVisible] = useState(true);
  const { t, i18n } = useTranslation('utilsV2');
  const auth = store.get(KEY);
  const KB_USER = auth?.auth_type === 'KB_USER';
  const from = sessionStorage.getItem(FROM);
  const modaolUrl =
    process.env.REACT_APP_SERVER_BASE_URL === 'stg'
      ? 'http://b2b-webview-stg.s3-website.ap-northeast-2.amazonaws.com'
      : 'http://b2b-webview-prd.s3-website.ap-northeast-2.amazonaws.com';
  // const [type, setPageType] = useState('main');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    pageType,
    pageTitle,
    firstOpen,
    nickname,
    inIframe,
    coupons,
    cartItems,
    accountInfo,
  } = useAppSelector(({ status, user, cart, account }) => ({
    pageType: status.pageType,
    pageTitle: status.pageTitle,
    firstOpen: status.firstOpen,
    nickname: user.users ? user.users?.[0]?.nickname : '',
    inIframe: status.inIframe,
    coupons: user.coupons,
    cartItems: cart.items,
    accountInfo: account.account,
    user,
  }));
  const navTab = useAppSelector(getMainCurrentTab);
  const medicalReceptionStep = useAppSelector(getMedicalReceptionStep);

  const [pathChanged, setPathChanged] = useState(false);

  // 안드로이드 뒤로가기 핸들링
  // useEffect(() => {
  //   const handlePopstate = () => {
  //     if (location.pathname.includes('/forehead')) {
  //       if (navTab !== 3) {
  //         // 촬영 후 이마촬영 히스토리 섹션으로 보내줌
  //         dispatch(setNavTab(3));
  //         window.scrollTo(0, document.body.scrollHeight);
  //       }
  //     }
  //   };
  //   window.addEventListener('popstate', handlePopstate);
  //   return () => window.removeEventListener('popstate', handlePopstate);
  // }, [location.pathname]);

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const prevPathname = usePrevious(location.pathname);

  useEffect(() => {
    if (prevPathname !== location.pathname) setPathChanged(true);
  }, [location.pathname, prevPathname]);

  useEffect(() => {
    if (pathChanged) {
      const timer = setTimeout(() => {
        setPathChanged(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pathChanged]);

  // 메인에서 scroll down -> 헤더 숨김, scroll up -> 헤더 나타남
  useEffect(() => {
    const threshold = 0.5;
    let lastScrollY = window.pageYOffset;

    if (pageType !== 'main') {
      setHeaderVisible(true);
    }

    const onScroll = () => {
      if (pageType === 'main') {
        if (firstOpen) {
          window.scrollTo(0, 0);
          return;
        }

        const scrollY = window.pageYOffset;
        if (scrollY > 50 && scrollY - lastScrollY > threshold) {
          // scroll down
          setHeaderVisible(false);
        } else if (lastScrollY - scrollY > threshold) {
          // scroll up
          setHeaderVisible(true);
        }
        lastScrollY = window.pageYOffset;
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [firstOpen, pageType]);

  /****** 헤더 타입 결정
  // main:               로고 + 장바구니 + 햄버거 + 내비게이션
  // zeroDepth:          타이틀
  // singleDepthSimple:  뒤로가기 + 타이틀
  // singleDepth:        뒤로가기 + 타이틀 + 햄버거
  // multiDepth:         뒤로가기 + 타이틀 + 장바구니 + 홈 + 햄버거  
  ******/
  useEffect(() => {
    if (inIframe) {
      if (location.pathname === '/') {
        dispatch(setPageType('main'));
      } else if (location.pathname === '/shampoo/search') {
        dispatch(setPageType('zeroDepth'));
      } else {
        dispatch(setPageType('singleDepthSimple'));
      }
      return;
    }
    if (location.pathname === '/') {
      dispatch(setPageType('main'));
      dispatch(setPageTitle(''));
    } else if (
      location.pathname === '/shampoo/search' ||
      location.pathname.includes('forehead') ||
      location.pathname === '/scalp/menu' ||
      location.pathname === '/scalp/compare/result'
    ) {
      dispatch(setPageType('singleDepth'));
    } else if (
      location.pathname === '/login' ||
      location.pathname.includes('register') ||
      location.pathname === '/adduser/profile' ||
      location.pathname.includes('forgot') ||
      location.pathname === '/account/update/password' ||
      location.pathname === '/opensource' ||
      location.pathname === '/version' ||
      location.pathname === '/terms' || // TODO : drawer 통해서 약관보는 경우만 구분할 수 있도록.. (회원가입 약관동의에서는 header 없음)
      location.pathname === '/terms/service' ||
      location.pathname === '/terms/privacy' ||
      location.pathname === '/terms/sensitivity' ||
      location.pathname === '/drawer/coupons' ||
      location.pathname === '/product/detail' ||
      location.pathname === '/telemedicine/medicationInformation' ||
      location.pathname === '/telemedicine/terms' ||
      (location.pathname.includes('/telemedicine') && from) // NOTE: 외부에서 원격진료 서비스로 진입한 유저는 원격진료 서비스에 격리
    ) {
      dispatch(setPageType('singleDepthSimple')); //뒤로가기 버튼 + 타이틀
    } else {
      dispatch(setPageType('multiDepth'));
    }
  }, [location, navTab]);

  const onPrevBtnClick = () => {
    if (location.pathname === '/shampoo/search') {
      history.push('/');
    } else if (location.pathname === '/forehead') {
      // CHECK : 촬영 후 메인페이지 메인탭으로 보내줌 (IOS의 경우 네이티브영역으로 갔다가 다시 돌아와도 상태를 간직하는것으로 보여짐, 때문에 촬영 후 백키 클릭시 이마체크 탭 으로 돌아오고 히스토리가 리프래쉬 되지않는 이슈가 있음 우선 메인탭으로 보내주는 방법으로 Hotfix 진행
      dispatch(setNavTab(0));
      history.push('/');
    } else if (location.pathname === '/telemedicine') {
      if (from === 'modaol') {
        return (window.location.href = modaolUrl);
      } else {
        history.push('/');
      }
    } else if (location.pathname === '/telemedicine/terms') {
      history.push('/telemedicine/medicalreception?redirectstep=2');
    } else if (location.pathname === '/telemedicine/medicalreception') {
      if (medicalReceptionStep === 0) {
        history.push('/telemedicine');
      } else {
        dispatch(setStep(medicalReceptionStep - 1));
        history.push('/telemedicine/medicalreception');
      }
    } else if (
      location.pathname === '/scalp/result' &&
      auth?.auth_type === 'KB_USER'
    ) {
      history.goBack();
      exitKbWebViewForKb();
    } else {
      history.goBack();
    }
  };

  if (
    location.pathname === '/' ||
    location.pathname.includes('quiz') ||
    location.pathname === '/product/detail' ||
    location.pathname === '/product/cart' ||
    location.pathname === '/payment/normal' ||
    location.pathname === '/analysis' ||
    location.pathname === '/kit/detail' ||
    location.pathname === '/scalp/result/v2' ||
    location.pathname.includes('shampoo') ||
    location.pathname === '/login' ||
    location.pathname.includes('/forehead') ||
    location.pathname === '/scalp/menu' ||
    location.pathname === '/scalp/guide' ||
    location.pathname === '/scalp/historys' ||
    location.pathname === '/scalp/result' ||
    location.pathname === '/hairloss-progress/result' ||
    location.pathname === '/scalp/compare/result' ||
    location.pathname.includes('register') ||
    location.pathname === '/adduser/profile' ||
    location.pathname.includes('forgot') ||
    location.pathname === '/account/update/password' ||
    location.pathname === '/opensource' ||
    location.pathname === '/version' ||
    location.pathname === '/terms' || // TODO : drawer 통해서 약관보는 경우만 구분할 수 있도록.. (회원가입 약관동의에서는 header 없음)
    location.pathname === '/terms/service' ||
    location.pathname === '/terms/privacy' ||
    location.pathname === '/terms/sensitivity' ||
    location.pathname === '/drawer/coupons' ||
    (location.pathname === '/telemedicine' &&
      sessionStorage.getItem(FROM) !== 'promotion') ||
    (location.pathname.includes('/telemedicine') &&
      location.pathname !== '/telemedicine')
  ) {
    return (
      <div
        className={`header ${headerVisible ? 'visible' : 'invisible'} ${isTeleMedicineService || (location.pathname === '/' && navTab !== 0)
          ? 'bg_white'
          : 'bg_paleLavender'
          } text_greyishBrown`}
      >
        <div
          className={`headerContainer ${isTeleMedicineService || (location.pathname === '/' && navTab !== 0)
            ? 'bg_white'
            : 'bg_paleLavender'
            }s`}
        >
          {pageType === 'main' ? (
            <>
              <div
                className={'header__logo ' + (inIframe ? 'inIframe' : '')}
                onClick={() => {
                  history.push('/');
                  dispatch(setNavTab(0));
                }}
              >
                <Logo />
              </div>

              {i18n.language === 'ko' && auth?.auth_type !== 'KB_USER' && (
                <div
                  className={'header__cart' + (inIframe ? 'inIframe' : '')}
                  onClick={() => {
                    if (nickname && accountInfo) {
                      goToMallCartV2Page(accountInfo.refresh_token);
                    } else {
                      //비로그인
                      history.push(`/login`);
                    }
                  }}
                >
                  {/* 아래 span 태그는 장바구니에 상품이 담겨 있을 경우 보여지는 빨간색 점 입니다. */}
                  {cartItems.length > 0 && <span></span>}
                  <Cart />
                </div>
              )}

              <div
                className={'header__greeting ' + (inIframe ? 'inIframe' : '')}
              >
                <Greeting
                  nickname={nickname}
                  firstOpen={firstOpen}
                  checkFirstOpen={() => dispatch(checkFirstOpen(false))}
                />
              </div>

              {/* <div className={'header__navbar ' + (inIframe ? 'inIframe' : '')}>
                <NavBar
                  firstOpen={firstOpen}
                  navTab={navTab}
                  setNavTab={(newNavTab) => dispatch(setNavTab(newNavTab))}
                  checkFirstOpen={() => dispatch(checkFirstOpen(false))}
                />
              </div> */}
            </>
          ) : (
            <>
              {pageType !== 'zeroDepth' && (
                <div
                  className={`header__goback ${pathChanged ? 'animate' : ''}`}
                  onClick={onPrevBtnClick}
                  style={{
                    display:
                      inIframe && location.pathname === '/' ? 'none' : 'block',
                  }}
                >
                  <ChevronLeft size={29} />
                </div>
              )}
              <div
                className={`header__title font_bold ${pathChanged ? 'animate' : ''
                  }`}
              >
                {pageTitle}
              </div>
            </>
          )}
          {pageType === 'multiDepth' && (
            <>
              {location.pathname !== '/product/cart' &&
                auth?.auth_type !== 'KB_USER' &&
                i18n.language === 'ko' && (
                  <div
                    className={'header__cart' + (inIframe ? 'inIframe' : '')}
                    onClick={() => {
                      if (nickname) {
                        history.push('/product/cart');
                      } else {
                        //비로그인
                        history.push(`/login`);
                      }
                    }}
                  >
                    {cartItems.length > 0 && <span></span>}
                    <Cart />
                  </div>
                )}

              <div className="header__home" onClick={() => history.push('/')}>
                <Home />
              </div>
            </>
          )}
          {pageType !== 'singleDepthSimple' && !inIframe && (
            <div
              className="header__hamburger"
              onClick={() => {
                if (accountInfo && !KB_USER) {
                  goToMallMypage(accountInfo.refresh_token);
                } else {
                  history.push('/drawer');
                }
              }}
            >
              <div></div>
              <div></div>
              {coupons.length > 0 && <span></span>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default Header;
